import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  type Step,
  WalkthroughDialogComponent,
  WalkthroughState,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';
import { walkthroughAssetsResolver } from '@core/walkthrough-util';

@Injectable({ providedIn: 'root' })
export class WalkthroughDashboardOverviewService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  private steps: Step[] = [
    {
      id: 'walkthrough-step-dashboard-overview-1',
      position: 1,
      component: {
        definition: WalkthroughDialogComponent,
        inputs: {
          heading: this.i18n.translate('app.dashboard.overview.walkthrough.step_1.heading'),
          description: this.i18n.translate('app.dashboard.overview.walkthrough.step_1.description'),
          imageUrl: walkthroughAssetsResolver('new_dashboard.svg'),
          ctaText: this.i18n.translate('app.dashboard.overview.walkthrough.step_1.cta_text'),
          closeText: this.i18n.translate('app.dashboard.overview.walkthrough.step_1.close_text'),
          closeIcon: undefined,
          showCloseButton: true,
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
    },
    {
      id: 'walkthrough-step-dashboard-overview-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'top'],
          heading: this.i18n.translate('app.dashboard.overview.walkthrough.step_2.heading'),
          description: this.i18n.translate('app.dashboard.overview.walkthrough.step_2.description'),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }],
      },
    },
    {
      id: 'walkthrough-step-dashboard-overview-3',
      position: 3,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['right', 'top'],
          heading: this.i18n.translate('app.dashboard.overview.walkthrough.step_3.heading'),
          description: this.i18n.translate('app.dashboard.overview.walkthrough.step_3.description'),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'end', overlayY: 'top' }],
        offset: { offsetX: 40, offsetY: 16 },
      },
    },
    {
      id: 'walkthrough-step-dashboard-overview-4',
      position: 4,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'bottom'],
          heading: this.i18n.translate('app.dashboard.overview.walkthrough.step_4.heading'),
          description: this.i18n.translate('app.dashboard.overview.walkthrough.step_4.description'),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' }],
      },
    },
    {
      id: 'walkthrough-step-dashboard-overview-5',
      position: 5,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'top'],
          heading: this.i18n.translate('app.dashboard.overview.walkthrough.step_5.heading'),
          description: this.i18n.translate('app.dashboard.overview.walkthrough.step_5.description'),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'bottom', overlayX: 'center', overlayY: 'top' }],
        offset: { offsetX: 8, offsetY: -10 },
      },
    },
    {
      id: 'walkthrough-step-dashboard-overview-6',
      position: 6,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'top'],
          heading: this.i18n.translate('app.dashboard.overview.walkthrough.step_6.heading'),
          description: this.i18n.translate('app.dashboard.overview.walkthrough.step_6.description'),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'bottom', overlayX: 'center', overlayY: 'top' }],
        offset: { offsetX: 8, offsetY: -10 },
      },
    },
    {
      id: 'walkthrough-step-dashboard-overview-7',
      position: 7,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'top'],
          heading: this.i18n.translate('app.dashboard.overview.walkthrough.step_7.heading'),
          description: this.i18n.translate('app.dashboard.overview.walkthrough.step_7.description'),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.got_it'),
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }],
        offset: { offsetX: 8, offsetY: -10 },
      },
    },
  ];

  public start(): void {
    this.walkthroughStepsService
      .start(this.steps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        if ([WalkthroughState.Finished, WalkthroughState.Canceled].includes(state)) {
          this.store.dispatch(
            FeatureDiscoveryActions.update({ payload: { dashboardsUsed: true } }),
          );
        }
      });
  }
}
